import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  flexRender,
  createColumnHelper,
  Table as TableType,
} from '@tanstack/react-table';

// Types
import {
  TableProps,
  ExternalCompanyRow,
  SearchbarFilterTypes,
  externalCompaniesColumnNames,
} from './types';

// Styled components
import {
  ContainerTable,
  ActiveCompaniesTableContainer,
  InactiveCompaniesTableContainer,
  FiltersContainer,
  TrStyled,
  SwapCompanyButtonRight,
  SwapCompanyButtonLeft,
  ActionCell,
  ActionButton,
  ActionIcon,
} from './styles';

// Compartidos de las tablas de gestión (los mismos que usa Usuarios)
import {
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
} from '../../../summaryTables/SummaryTablesStyles';
import {
  InputRadioContainer,
  InputRadioStyled,
} from '../../../components/MainStyledComponents';
import { Typography } from '../../../../Typography';
import SearchBar from '../../../../../inputs/SearchBar';

// Icons
import EditIcon from '../../../../../../assets/icons/edit-icon.svg';
import RemoveIcon from '../../../../../../assets/icons/remove-icon.svg';
import { matchesSearch } from '../../../../../../utils/textSearch';

/**
 * Catálogo de empresas externas del módulo de Gestión.
 *
 * Layout y mecánica calcados de `UsersManagementTable`: dos tablas EN FILA
 * (activas | inactivas), selección por radio y flechas ⮕ / ⬅ debajo de cada
 * una para moverlas de un lado al otro.
 *
 * Sobre esa base se añade lo propio de este catálogo (patrón de Equipos):
 * acciones de fila para renombrar y eliminar, que abren su modal — la tabla
 * solo las pide, nunca las ejecuta.
 *
 * Columnas: **Empresa** (el dato), **Usos** (nº de actuaciones asociadas — es
 * lo que decide si se puede borrar en duro) y **Acciones**. No hay más porque
 * la entidad no tiene más: es un nombre.
 */
export const ExternalCompaniesManagementTable = ({
  data,
  onDeactivate,
  onActivate,
  onEditCompany,
  onDeleteCompany,
}: TableProps): JSX.Element => {
  const { t } = useTranslation(['pages', 'tables', 'buttons']);

  const [activeData, setActiveData] = useState(data.activeCompanies);
  const [inactiveData, setInactiveData] = useState(data.inactiveCompanies);

  useEffect(() => {
    setActiveData(data.activeCompanies);
    setInactiveData(data.inactiveCompanies);
  }, [data]);

  const [activeChecked, setActiveChecked] = useState({});
  const [inactiveChecked, setInactiveChecked] = useState({});

  const columnHelper = createColumnHelper<ExternalCompanyRow>();

  const columns = useMemo(
    () => [
      columnHelper.display({
        id: 'select',
        cell: ({ row }) => (
          <InputRadioContainer>
            <InputRadioStyled
              name="externalCompanyRadio"
              {...{
                checked: row.getIsSelected(),
                disabled: !row.getCanSelect(),
                indeterminate: row.getIsSomeSelected(),
                onChange: row.getToggleSelectedHandler(),
              }}
            />
          </InputRadioContainer>
        ),
      }),
      columnHelper.accessor((row) => row.companyName, {
        id: externalCompaniesColumnNames.companyName.id,
        header: () => t(externalCompaniesColumnNames.companyName.labelKey),
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor((row) => row.externalWorksCount, {
        id: externalCompaniesColumnNames.externalWorksCount.id,
        header: () =>
          t(externalCompaniesColumnNames.externalWorksCount.labelKey),
        cell: (info) => info.getValue() ?? 0,
      }),
      columnHelper.display({
        id: 'actions',
        header: () => t('tables:headers.actions'),
        cell: ({ row }) => {
          const inUse = row.original.externalWorksCount > 0;
          return (
            <ActionCell>
              <ActionButton
                type="button"
                title={t('buttons:edit')}
                onClick={(e) => {
                  e.stopPropagation();
                  onEditCompany(row.original);
                }}
              >
                <ActionIcon src={EditIcon} alt={t('buttons:edit')} />
              </ActionButton>
              <ActionButton
                type="button"
                disabled={inUse}
                title={
                  inUse
                    ? t('pages:management.externalCompanyInUse')
                    : t('buttons:delete')
                }
                onClick={(e) => {
                  e.stopPropagation();
                  onDeleteCompany(row.original);
                }}
              >
                <ActionIcon src={RemoveIcon} alt={t('buttons:delete')} />
              </ActionButton>
            </ActionCell>
          );
        },
      }),
    ],
    [onEditCompany, onDeleteCompany, t]
  );

  const activeSearchFilter = useCallback<SearchbarFilterTypes>(
    (value) => {
      setActiveData(
        value === ''
          ? data.activeCompanies
          : data.activeCompanies.filter((company) =>
            matchesSearch([company.companyName], value)
          )
      );
    },
    [data.activeCompanies]
  );

  const inactiveSearchFilter = useCallback<SearchbarFilterTypes>(
    (value) => {
      setInactiveData(
        value === ''
          ? data.inactiveCompanies
          : data.inactiveCompanies.filter((company) =>
            matchesSearch([company.companyName], value)
          )
      );
    },
    [data.inactiveCompanies]
  );

  const activeTable = useReactTable({
    getRowId: (row) => row.id,
    data: activeData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onRowSelectionChange: setActiveChecked,
    state: { rowSelection: activeChecked },
    enableRowSelection: true,
    enableMultiRowSelection: false,
  });

  const inactiveTable = useReactTable({
    getRowId: (row) => row.id,
    data: inactiveData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onRowSelectionChange: setInactiveChecked,
    state: { rowSelection: inactiveChecked },
    enableRowSelection: true,
    enableMultiRowSelection: false,
  });

  const renderTable = (table: TableType<ExternalCompanyRow>) => (
    <TableContainer>
      <Table>
        <Thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <Th key={header.id}>
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    )}
                </Th>
              ))}
            </tr>
          ))}
        </Thead>
        <Tbody>
          {table.getRowModel().rows.map((row) => (
            <TrStyled
              key={row.id}
              checked={row.getIsSelected()}
              indeterminate={!table.getIsSomeRowsSelected()}
              onClick={() => row.getToggleSelectedHandler()(row)}
            >
              {row.getVisibleCells().map((cell) => (
                <Td key={cell.id}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </Td>
              ))}
            </TrStyled>
          ))}
        </Tbody>
      </Table>
    </TableContainer>
  );

  const moveSelected = (
    table: TableType<ExternalCompanyRow>,
    action: (id: string) => Promise<void>
  ) => {
    const selected = table.getSelectedRowModel().rows[0];
    if (!selected) return;
    void action(selected.original.id);
    table.resetRowSelection(true);
  };

  return (
    <ContainerTable>
      <ActiveCompaniesTableContainer>
        <Typography size="L" variant="semiBold">
          {t('pages:management.activeExternalCompanies')} ({activeData.length})
        </Typography>
        <FiltersContainer>
          <SearchBar
            variant="form"
            onChangeInput={(e) => activeSearchFilter(e.target.value)}
          />
        </FiltersContainer>
        {renderTable(activeTable)}
        <SwapCompanyButtonRight
          type="button"
          title={t('pages:management.deactivate')}
          disabled={activeTable.getSelectedRowModel().rows.length === 0}
          onClick={() => moveSelected(activeTable, onDeactivate)}
        >
          ⮕
        </SwapCompanyButtonRight>
      </ActiveCompaniesTableContainer>

      <InactiveCompaniesTableContainer>
        <Typography size="L" variant="semiBold">
          {t('pages:management.inactiveExternalCompanies')} (
          {inactiveData.length})
        </Typography>
        <FiltersContainer>
          <SearchBar
            variant="form"
            onChangeInput={(e) => inactiveSearchFilter(e.target.value)}
          />
        </FiltersContainer>
        {renderTable(inactiveTable)}
        <SwapCompanyButtonLeft
          type="button"
          title={t('pages:management.activate')}
          disabled={inactiveTable.getSelectedRowModel().rows.length === 0}
          onClick={() => moveSelected(inactiveTable, onActivate)}
        >
          ⬅
        </SwapCompanyButtonLeft>
      </InactiveCompaniesTableContainer>
    </ContainerTable>
  );
};
